import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  TextField, 
  Stepper, 
  Step, 
  StepLabel, 
  Box,
  Alert,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { 
  Security as SecurityIcon, 
  Password as PasswordIcon, 
  CheckCircle as CheckCircleIcon,
  Fingerprint as FingerprintIcon,
  PhoneAndroid as PhoneIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { validatePassword, PasswordRequirements } from '../utils/passwordValidation';
import WebAuthnSetup from '../components/WebAuthn/WebAuthnSetup';
import TOTPSetup from '../components/TOTP/TOTPSetup';
import ThemeBackground from '../components/Layout/ThemeBackground';

interface SetupSteps {
  PASSWORD: 0;
  MFA_SETUP: 1;
  COMPLETE: 2;
}

const STEPS: (keyof SetupSteps)[] = ['PASSWORD', 'MFA_SETUP', 'COMPLETE'];

const STEP_LABELS = {
  PASSWORD: 'Set Password',
  MFA_SETUP: 'Setup Multi-Factor Authentication',
  COMPLETE: 'Setup Complete'
};

const STEP_DESCRIPTIONS = {
  PASSWORD: 'Create a secure password for your account',
  MFA_SETUP: 'Add biometric or security key authentication for enhanced security',
  COMPLETE: 'Your account is now secure and ready to use'
};

function InitialSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  
  // Form states
  const [currentStep, setCurrentStep] = useState<keyof SetupSteps>('PASSWORD');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setPasswordSet] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<'webauthn' | 'totp'>('totp'); // Default to TOTP

  // Check if user needs initial setup
  useEffect(() => {
    const fromPasswordSetup = (location.state as any)?.fromPasswordSetup;
    
    if (!user?.requirePasswordChange && !fromPasswordSetup) {
      // User doesn't need setup, redirect to dashboard
      navigate('/dashboard');
    } else if (fromPasswordSetup) {
      // Coming from password setup, skip to MFA step
      setPasswordSet(true);
      setCurrentStep('MFA_SETUP');
    }
  }, [user, navigate, location.state]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      setLoading(false);
      return;
    }

    try {
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      setPasswordSet(true);
      setCurrentStep('MFA_SETUP');
      setError('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          'Failed to change password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetupComplete = () => {
    setMfaSetup(true);
    setCurrentStep('COMPLETE');
  };

  // Skip MFA function removed - MFA is now required for security

  const handleCompleteSetup = async () => {
    // Ensure MFA is actually set up before completing
    if (!mfaSetup) {
      setError('Multi-factor authentication setup is required to complete account setup.');
      return;
    }

    try {
      await api.post('/auth/complete-initial-setup', {
        mfaEnabled: true
      });
      
      // Complete setup and redirect
      logout();
      navigate('/login', { 
        state: { 
          message: 'Account setup complete! Your account is now secured with multi-factor authentication.' 
        }
      });
    } catch (error) {
      console.error('Error completing setup:', error);
      setError('Failed to complete setup. Please try again.');
    }
  };

  const renderPasswordStep = () => (
    <div className="glass-card max-w-md mx-auto p-6">
      <div className="text-center mb-6">
        <PasswordIcon sx={{ fontSize: 48, color: '#6366f1', mb: 2 }} />
        <Typography variant="h5" gutterBottom className="theme-text-primary">
          Set Your Password
        </Typography>
        <Typography variant="body2" className="theme-text-secondary">
          Create a secure password for your account
        </Typography>
      </div>

      <form onSubmit={handlePasswordSubmit}>
        <TextField
          fullWidth
          type="password"
          label="Current Password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          margin="normal"
          required
          helperText="Enter the password you received when your account was created"
          sx={{
            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '&.Mui-focused fieldset': { borderColor: '#6366f1' },
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            },
            '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
          }}
        />
        
        <TextField
          fullWidth
          type="password"
          label="New Password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          margin="normal"
          required
          sx={{
            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '&.Mui-focused fieldset': { borderColor: '#6366f1' },
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        />
        
        <TextField
          fullWidth
          type="password"
          label="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          required
          sx={{
            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '&.Mui-focused fieldset': { borderColor: '#6366f1' },
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        />

        <Box sx={{ mt: 2, mb: 3 }}>
          <PasswordRequirements password={newPassword} />
        </Box>

        {error && (
          <Alert severity="error" sx={{ 
            mt: 2, 
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            color: '#fca5a5'
          }}>
            {error.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ 
            mt: 3,
            backgroundColor: '#4f46e5',
            '&:hover': { backgroundColor: '#4338ca' },
            '&:disabled': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Set Password'}
        </Button>
      </form>
    </div>
  );

  const renderMfaStep = () => (
    <div className="glass-card max-w-2xl mx-auto p-6">
      <div className="text-center mb-6">
        {mfaMethod === 'webauthn' ? (
          <FingerprintIcon sx={{ fontSize: 48, color: '#6366f1', mb: 2 }} />
        ) : (
          <PhoneIcon sx={{ fontSize: 48, color: '#6366f1', mb: 2 }} />
        )}
        <Typography variant="h5" gutterBottom className="theme-text-primary">
          Setup Multi-Factor Authentication
        </Typography>
        <Typography variant="body2" className="theme-text-secondary mb-4">
          Choose your preferred authentication method
        </Typography>
        
        <Alert severity="info" sx={{ 
          mb: 3, 
          textAlign: 'left',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          color: '#93c5fd'
        }}>
          <Typography variant="body2">
            <strong>Why MFA?</strong> Multi-factor authentication protects your account even if your password is compromised. 
            Choose between biometric/security key or authenticator app.
          </Typography>
        </Alert>

        {/* MFA Method Toggle */}
        <ToggleButtonGroup
          value={mfaMethod}
          exclusive
          onChange={(e, newMethod) => {
            if (newMethod !== null) {
              setMfaMethod(newMethod);
              setError(''); // Clear any errors when switching
            }
          }}
          fullWidth
          sx={{ mb: 3 }}
        >
          <ToggleButton 
            value="totp"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.23)',
              '&.Mui-selected': {
                backgroundColor: '#4f46e5',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#4338ca'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            <PhoneIcon sx={{ mr: 1 }} />
            Authenticator App
          </ToggleButton>
          <ToggleButton 
            value="webauthn"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.23)',
              '&.Mui-selected': {
                backgroundColor: '#4f46e5',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#4338ca'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            <FingerprintIcon sx={{ mr: 1 }} />
            Biometric / Security Key
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {mfaMethod === 'webauthn' ? (
        <WebAuthnSetup 
          onSetupComplete={handleMfaSetupComplete}
          onError={setError}
        />
      ) : (
        <TOTPSetup 
          onSetupComplete={handleMfaSetupComplete}
          onError={setError}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ 
          mt: 2,
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          color: '#fca5a5',
          whiteSpace: 'pre-wrap'
        }}>
          <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
            {error}
          </Typography>
        </Alert>
      )}

      {/* Skip option removed for security - MFA is required */}
    </div>
  );

  const renderCompleteStep = () => (
    <div className="glass-card max-w-md mx-auto p-6 text-center">
      <CheckCircleIcon sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
      <Typography variant="h5" gutterBottom className="theme-text-primary">
        Setup Complete!
      </Typography>
      <Typography variant="body1" className="theme-text-secondary mb-6">
        Your account is now secure and ready to use.
      </Typography>

      <div className="mb-6">
        <Chip 
          icon={<CheckCircleIcon />} 
          label="Password Set" 
          sx={{ 
            mr: 1, 
            mb: 1,
            backgroundColor: 'rgba(16, 185, 129, 0.2)',
            color: '#6ee7b7',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }} 
        />
        {mfaSetup && (
          <Chip 
            icon={<SecurityIcon />} 
            label="MFA Enabled" 
            sx={{ 
              mr: 1, 
              mb: 1,
              backgroundColor: 'rgba(16, 185, 129, 0.2)',
              color: '#6ee7b7',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }} 
          />
        )}
      </div>

      <Button
        fullWidth
        variant="contained"
        onClick={handleCompleteSetup}
        size="large"
        sx={{
          backgroundColor: '#4f46e5',
          '&:hover': { backgroundColor: '#4338ca' },
          py: 1.5
        }}
      >
        Continue to Login
      </Button>
    </div>
  );

  const getStepIndex = () => STEPS.indexOf(currentStep);

  return (
    <ThemeBackground variant="full">
      <Box sx={{ minHeight: '100vh', py: 4 }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 2 }}>
          {/* Header */}
          <div className="text-center mb-8">
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: 600,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Account Setup
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1.1rem',
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              Welcome! Let's secure your account with a password and multi-factor authentication.
            </Typography>
          </div>

          {/* Stepper */}
          <Stepper 
            activeStep={getStepIndex()} 
            sx={{ 
              mb: 4,
              '& .MuiStepLabel-label': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiStepLabel-label.Mui-active': { color: 'white' },
              '& .MuiStepLabel-label.Mui-completed': { color: '#10b981' },
              '& .MuiStepIcon-root': { color: 'rgba(255, 255, 255, 0.3)' },
              '& .MuiStepIcon-root.Mui-active': { color: '#6366f1' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#10b981' }
            }}
          >
            {STEPS.map((step) => (
              <Step key={step}>
                <StepLabel>
                  <Typography variant="body2">
                    {STEP_LABELS[step]}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <div className="mb-8">
            <Typography 
              variant="h6" 
              className="text-center mb-12 font-medium"
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.1rem',
                fontWeight: 500
              }}
            >
              {STEP_DESCRIPTIONS[currentStep]}
            </Typography>

            {currentStep === 'PASSWORD' && renderPasswordStep()}
            {currentStep === 'MFA_SETUP' && renderMfaStep()}
            {currentStep === 'COMPLETE' && renderCompleteStep()}
          </div>
        </Box>
      </Box>
    </ThemeBackground>
  );
}

export default InitialSetup;
